import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { CalendarEvent, CalendarMonthViewBeforeRenderEvent, CalendarMonthViewDay, CalendarView } from 'angular-calendar';
import { addDays, addMonths, addWeeks, endOfDay, endOfMonth, endOfWeek, isSameDay, startOfDay, startOfMonth, startOfWeek, subDays, subMonths, subWeeks } from 'date-fns';
import moment from 'moment';
import { Subject } from 'rxjs';

type CalendarPeriod = 'day' | 'week' | 'month';

function addPeriod(period: CalendarPeriod, date: Date, amount: number): Date {
  return {
    day: addDays,
    week: addWeeks,
    month: addMonths,
  }[period](date, amount);
}

function startOfPeriod(period: CalendarPeriod, date: Date): Date {
  return {
    day: startOfDay,
    week: startOfWeek,
    month: startOfMonth,
  }[period](date);
}

function subPeriod(period: CalendarPeriod, date: Date, amount: number): Date {
  return {
    day: subDays,
    week: subWeeks,
    month: subMonths,
  }[period](date, amount);
}

function endOfPeriod(period: CalendarPeriod, date: Date): Date {
  return {
    day: endOfDay,
    week: endOfWeek,
    month: endOfMonth
  }[period](date);
}

@Component({
  selector: 'app-multiple-date-selector',
  templateUrl: './multiple-date-selector.component.html',
  styleUrls: ['./multiple-date-selector.component.css']
})
export class MultipleDateSelectorComponent implements OnInit {

  //Defaulting to month view and today's date as current
  view: CalendarView | CalendarPeriod = CalendarView.Month;
  viewDate: Date = new Date();
  excludeDays: number[] = [];

  //Read special dates from main component
  @Input() events: CalendarEvent[] = [];
  @Input() locale: string = 'en';
  @Input() selectedDate: any[] = [];
  @Input() firstSelectableDate: Date = new Date();
  @Input() isVacationDashAdmin: boolean = false;

  //Notify the selected date
  @Output() selectedDateChange = new EventEmitter<any[]>();
  @Output() calculateTimeOff: EventEmitter<any> = new EventEmitter();

  //Disable the calendar selection based on the user role
  @Input() minDate: Date;
  @Input() maxDate: Date;
  prevBtnDisabled: boolean = false;
  nextBtnDisabled: boolean = false;

  //Disable the weekend
  @Input() disableWeekendTimeOff: boolean = false;

  newTimeOffClass: string = 'new-time-off-date';
  refresh = new Subject<void>();
  activeDayIsOpen: boolean;

  constructor() {
    this.dateOrViewChanged();
  }

  ngOnInit(): void {
    this.refreshSelectableDate();
    this.excludeDays = this.disableWeekendTimeOff ? [0, 6] : [];
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes != null && changes.firstSelectableDate != null) {
      var fromDate = this.firstSelectableDate;
      fromDate.setHours(0, 0, 0, 0);
      var tempDates = [];
      this.selectedDate.forEach((selection: any) => {
        if (selection.date >= fromDate) {
          tempDates.push(selection);
        }
      });
      this.selectedDate = tempDates;
      this.selectedDateChange.emit(this.selectedDate);
      this.refreshSelectableDate();
      this.dateOrViewChanged();
      this.refreshView();
    }
  }

  private refreshSelectableDate() {
    this.minDate = this.firstSelectableDate != null ? new Date(this.firstSelectableDate.setHours(0, 0, 0, 0)) : new Date();
    this.maxDate = this.isVacationDashAdmin ? moment(this.firstSelectableDate).add('100', 'years').toDate() : moment(this.firstSelectableDate).add('1', 'years').toDate();
  }

  private dateIsValid(date: Date): boolean {
    return date >= this.minDate && date <= this.maxDate;
  }

  private refreshView(): void {
    this.refresh.next();
  }

  increment(): void {
    this.activeDayIsOpen = false;
    this.changeDate(addPeriod(this.view, this.viewDate, 1));
  }

  decrement(): void {
    this.activeDayIsOpen = false;
    this.changeDate(subPeriod(this.view, this.viewDate, 1));
  }

  today(): void {
    this.changeDate(new Date());
  }

  private changeDate(date: Date): void {
    this.viewDate = date;
    this.dateOrViewChanged();
  }

  private dateOrViewChanged(): void {
    this.prevBtnDisabled = !this.dateIsValid(
      endOfPeriod(this.view, subPeriod(this.view, this.viewDate, 1))
    );
    this.nextBtnDisabled = !this.dateIsValid(
      startOfPeriod(this.view, addPeriod(this.view, this.viewDate, 1))
    );
    if (this.viewDate < this.minDate) {
      this.changeDate(this.minDate);
    } else if (this.viewDate > this.maxDate) {
      this.changeDate(this.maxDate);
    }
  }

  beforeMonthViewRender(renderEvent: CalendarMonthViewBeforeRenderEvent): void {
    //Process existing dates
    renderEvent.body.forEach((day) => {
      this.events.forEach((item) => {
        if (moment(day.date).isSame(item.start)) {
          if (item.meta.IsPartialRequest) {
            day.cssClass = "partial-time-off-date-" + item.meta.Category;
          }
          else {
            day.cssClass = "existing-time-off-date-" + item.meta.Category;
          }
        }
      });
      this.selectedDate.forEach((selection) => {
        if (moment(day.date).isSame(selection.date)) {
          day.cssClass = selection.cssClass;
        }
      });
      if (!this.dateIsValid(day.date)) {
        day.cssClass = 'cal-disabled';
      }
    });
  }


  dayClicked(day: CalendarMonthViewDay): void {
    const selectedDateTime = day.date.getTime();
    const eventDateIndex = this.events.findIndex(
      (existingEvent) => existingEvent.start.getTime() === selectedDateTime
    );
    const selectedDateIndex = this.selectedDate.findIndex(
      (selectedDate) => selectedDate.date.getTime() === selectedDateTime
    );

    //Display the holiday event title
    if ((isSameDay(this.viewDate, day.date) && this.activeDayIsOpen === true) || day.events.length === 0) {
      this.activeDayIsOpen = false;
    }
    else {
      if (day.events[0].meta != null && day.events[0].meta.Category == "H") {
        this.activeDayIsOpen = true;
      } else {
        this.activeDayIsOpen = false;
      }
    }
    //Move the month based on selected date.
    this.viewDate = day.date;

    if (day.events.length > 0 && day.events.length > 0) {
      if (day.cssClass != this.newTimeOffClass && day.cssClass.indexOf("partial") == -1) {
        return;  // Do not allow selection of existing timeoff dates
      }
    }

    if (eventDateIndex != -1) {
      var cssClass = day.cssClass;
      //togged css class if its a partial date, else remove selected date
      switch (cssClass) {
        case "partial-time-off-date-P":
          day.cssClass = "new-partial-P";
          this.selectedDate.push(day);
          break;
        case "partial-time-off-date-A":
          day.cssClass = "new-partial-A";
          this.selectedDate.push(day);
          break;
        case "partial-time-off-date-H":
          day.cssClass = "new-partial-H";
          this.selectedDate.push(day);
          break;
        case "partial-time-off-date-N":
          day.cssClass = "new-partial-N";
          this.selectedDate.push(day);
          break;
        case "new-partial-P":
          day.cssClass = "partial-time-off-date-P";
          this.selectedDate.splice(selectedDateIndex, 1);
          break;
        case "new-partial-A":
          day.cssClass = "partial-time-off-date-A";
          this.selectedDate.splice(selectedDateIndex, 1);
          break;
        case "new-partial-H":
          day.cssClass = "partial-time-off-date-H";
          this.selectedDate.splice(selectedDateIndex, 1);
          break;
        case "new-partial-N":
          day.cssClass = "partial-time-off-date-N";
          this.selectedDate.splice(selectedDateIndex, 1);
          break;
        default:
          this.selectedDate.splice(selectedDateIndex, 1);
          break;
      }
    }
    else {
      //if date was not found, add new date to selection
      if (selectedDateIndex != -1 && day.cssClass == this.newTimeOffClass) {
        delete day.cssClass;
        this.selectedDate.splice(selectedDateIndex, 1);
      }
      else {
        day.cssClass = this.newTimeOffClass;
        this.selectedDate.push(day);
      }
    }

    if(this.selectedDate != null && this.selectedDate.length > 1) {
      this.selectedDate = this.selectedDate.sort((date1, date2) => date1.date < date2.date ? -1 : 1);
    }

    this.selectedDateChange.emit(this.selectedDate);
    this.calculateTimeOff.emit();
  }
}
